import { useState, useCallback, useEffect, useRef, useEffectEvent } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import {
  startOffersAudit,
  getAuditStatus,
  getAuditResults,
  cancelAudit,
  exportAuditResults,
  saveKnownIssue as apiSaveKnownIssue,
  saveBaseline,
  deleteBaseline,
  deleteAudit,
  type AuditHistoryItem,
} from '../api'
import OffersAuditInstructions from './OffersAuditInstructions'
import AppLayout from '../components/AppLayout'
import HistoryPanel from '../components/HistoryPanel'
import OffersAuditHistory from '../components/OffersAuditHistory'

// TDA Data - sorted alphabetically by code
const TDA_LIST = [
  { code: 'BOS', name: 'New England' },
  { code: 'CAT', name: 'Central Atlantic Toyota' },
  { code: 'CHI', name: 'Chicago Region' },
  { code: 'CIN', name: 'Cincinnati' },
  { code: 'CON', name: 'Connecticut' },
  { code: 'DVR', name: 'Denver' },
  { code: 'GNY', name: 'Greater New York' },
  { code: 'GOC', name: 'Gold Coast' },
  { code: 'GST', name: 'Gulf States Toyota' },
  { code: 'INW', name: 'Inland North West' },
  { code: 'KAC', name: 'Kansas City Region' },
  { code: 'NCA', name: 'Northern California' },
  { code: 'PHX', name: 'Phoenix Valley' },
  { code: 'PTL', name: 'Portland Metro' },
  { code: 'SET', name: 'Southeast Toyota' },
  { code: 'SND', name: 'San Diego Metro' },
  { code: 'SOC', name: 'Southern California' },
  { code: 'TRE', name: 'Tri East' },
  { code: 'TRI', name: 'Tri State' },
  { code: 'UST', name: 'Up State' },
  { code: 'WWN', name: 'Western Washington' },
]

// Format page names for display (e.g., "offerDetails" -> "Offer Details")
const formatPageName = (page: string): string => {
  const pageNames: Record<string, string> = {
    homepage: 'Homepage',
    offers: 'Offers',
    offerDetails: 'Offer Details',
    vehicleInfoSupra: 'VIP: GR Supra',
    vehicleInfoLandCruiser: 'VIP: Land Cruiser',
  }
  return pageNames[page] || page
}

// Convert backend diffPercent to a display match percentage. "100" is reserved for a
// pixel-identical comparison: anything with a real diff floors to one decimal (capped
// at 99.9) so a 0.4% diff reads "99.6% match", never "100% match" next to callouts.
const computeMatchPercentage = (diffPercent: number): number => {
  if (diffPercent === 0) return 100
  const raw = (1 - diffPercent / 100) * 100
  return Math.max(0, Math.min(99.9, Math.floor(raw * 10) / 10))
}

// Format for display: whole numbers stay clean ("100% match"), partial matches keep
// their one decimal ("99.6% match"). -1 is the no-baseline sentinel, handled by callers.
const formatMatchLabel = (matchPercentage: number): string =>
  `${Number.isInteger(matchPercentage) ? matchPercentage : matchPercentage.toFixed(1)}% match`

const MATCH_TOOLTIP =
  'Match % measures pixel similarity vs the approved baseline. Callouts below can come from ' +
  'DOM and data checks (carousel, broken images, offer data) that are independent of pixels, ' +
  'so a high match can still have callouts.'


// Badge label for a visual result. RCT panes derive their name from the offerId slug
// ('cat:trucks' -> 'CATEGORY: TRUCKS', 'btype:lease' -> 'OFFER TYPE: LEASE').
const formatOfferTypeBadge = (offerType: string, offerId?: string): string => {
  const slug = (offerId || '').replace(/^(cat|btype):/, '').replace(/-/g, ' ').toUpperCase()
  if (offerType === 'category') return slug ? `CATEGORY: ${slug}` : 'CATEGORY'
  if (offerType === 'browseType') return slug ? `OFFER TYPE: ${slug}` : 'OFFER TYPE'
  if (offerType === 'multiple_vehicle') return 'MULTI VEHICLE'
  return offerType.replace('_', ' ').toUpperCase()
}

// Context column for the Data Issues table, derived from the result row an issue came
// from: the category pane ("Trucks"), browse-type pane ("Lease") or offer-detail type
// ("APR") the capture was keyed to. These are mutually exclusive per capture, so one
// column covers them all. "-" when none apply (e.g. BAT rows or default page shots).
const titleCaseSlug = (slug: string): string =>
  slug
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bApr\b/g, 'APR')
    .replace(/\bSuvs\b/g, 'SUVs')

// Display cleanup for a raw issue string: strips bracket prefixes that just repeat other columns
// ([OFFER DETAIL] == Page, [RCT WARNING]/[RCT ERROR] == Severity) and the chevron's "Arrow Icon"
// screen-reader cruft. Keeps [OAT vs TOOL]. Raw text is preserved on DataIssue.field for matching.
const cleanIssueFieldText = (raw: string): string => {
  if (!raw) return raw
  let s = raw.replace(/^\[(?:OFFER DETAIL|RCT WARNING|RCT ERROR)\]\s*/i, '')
  s = s.replace(/\s+(?:Arrow Icon|Chevron Icon|Chevron)(?=["”'’]|\s*(?:->|→)|\s*$)/gi, '')
  return s.trim()
}

// offerType values from the backend are inconsistently cased ("APR", "Lease", "Multiple_Vehicle",
// "category", "browseType"), so normalize before comparing.
const normType = (offerType?: string): string => (offerType || '').toLowerCase().replace(/[\s-]+/g, '_')

// Standardized offer-type label for the Type column.
const formatType = (offerType?: string, offerId?: string): string | null => {
  if (!offerType) return null
  const n = normType(offerType)
  const slug = (offerId || '').replace(/^(cat|btype):/, '')
  if (n === 'apr') return 'APR'
  if (n === 'lease') return 'Lease'
  if (n === 'cash') return 'Cash'
  if (n === 'multiple_vehicle') return 'Multi Vehicle'
  if (n === 'category' || n === 'browsetype') return slug ? titleCaseSlug(slug) : null
  return titleCaseSlug(offerType)
}

// Vehicle name for the Vehicle(s) column. Prefers the pretty name from the finding text
// ("... for Grand Highlander Lease (offerId ...)"), falls back to the offerId series slug.
// Multi-vehicle and miscellaneous offers cover many models (their seriesName is a long list),
// so they collapse to "Multiple" instead of dumping the whole list.
const parseVehicleName = (
  offerType?: string,
  offerId?: string,
  raw?: string
): { vehicle: string | null; multi: boolean } => {
  const n = normType(offerType)
  if (n === 'multiple_vehicle' || n === 'miscellaneous') return { vehicle: null, multi: true }
  const tooLong = (v: string) => v.length > 40 || v.split(/\s+/).length > 3
  const m = (raw || '').match(/for\s+(.+?)\s+(?:APR|Lease|Cash|Miscellaneous)\s+\(offerId/i)
  if (m) {
    const v = m[1].trim()
    return tooLong(v) ? { vehicle: null, multi: true } : { vehicle: v, multi: false }
  }
  if (offerId) {
    const seg = offerId.match(/^([a-z0-9]+?)_(?:apr|lease|cash|misc\w*|multiple_vehicle)_/i)
    if (seg) return { vehicle: titleCaseSlug(seg[1]), multi: false }
  }
  return { vehicle: null, multi: false }
}

export interface IssueClassification {
  label: string
  detail: string
  status?: string
  linkLabel?: string
  linkUrl?: string
}

// Map a raw finding string to a standardized Issue label + supplementary detail. Any finding that
// doesn't match a known pattern becomes "Other" with its cleaned text preserved, so nothing is lost.
export const classifyIssue = (raw: string): IssueClassification => {
  const isOAT = /^\s*\[OAT vs TOOL\]/i.test(raw)
  const s = cleanIssueFieldText(raw).replace(/^\[OAT vs TOOL\]\s*/i, '')
  const has = (re: RegExp) => re.test(s)

  // Trailing text after the URL is the plain-language explanation (hidden-space cause, intended
  // destination, offer count) — show it in the detail cell instead of dropping it.
  const link = s.match(/Marketing link broken\s*\(([^)]+)\):\s*["“]([^"”]*)["”]\s*(?:->|→)\s*(\S+)\s*(.*)/i)
  if (link) return { label: 'Marketing link broken', status: link[1], linkLabel: link[2], linkUrl: link[3], detail: (link[4] || '').trim() }
  // The label says it all; the only detail is the generic "pulled or URL changed" note, so drop it.
  if (has(/^Offer detail page did not load/i)) return { label: 'Offer page not found', detail: '' }
  // Aggregate triage notes (one per region+language) for offers whose vehicle/model year isn't on
  // the site yet, and for runs that started before the month's offers went live.
  if (has(/vehicle\/model year is not on the site yet/i)) return { label: 'Vehicle not MYCOed yet', detail: s }
  if (has(/have not gone live on the site yet/i)) return { label: 'Offers not live yet', detail: s }
  if (isOAT) return { label: 'OAT / site mismatch', detail: s }
  if (has(/^Expiration mismatch/i)) return { label: 'Expiration date mismatch', detail: s.replace(/^Expiration mismatch[^:]*:\s*/i, '') }
  if (has(/was not found on the detail page/i)) return { label: 'Offer value mismatch', detail: s }
  if (has(/^Broken image\(s\) on the/i)) return { label: 'Broken image on offer page', detail: s }
  if (has(/no vehicle images loaded|missing image confirmed|missing vehicle image/i)) return { label: 'Missing vehicle image', detail: s }
  if (has(/change-vehicle picker has broken/i)) return { label: 'Broken change-vehicle image', detail: s }
  if (has(/call out: region has a (?:college|military)|expected (?:college|military) image|college\/military offer is missing/i))
    return { label: 'Missing College/Military badge', detail: s.replace(/^CALL OUT:\s*/i, '') }
  if (has(/id\.me/i)) return { label: 'Missing ID.me link', detail: s }
  if (has(/vcr fallback/i)) return { label: 'VCR fallback image', detail: s }
  if (has(/featured-vehicles module has broken/i)) return { label: 'Broken carousel image', detail: s }
  if (has(/broken image or missing price|explore/i)) return { label: 'Broken category image / price', detail: s }
  if (has(/kbb link found|buyatoyota\.com/i)) return { label: 'BAT link on RCT page', detail: s }
  if (has(/dealer url may not match/i)) return { label: 'Dealer URL mismatch', detail: s }
  if (has(/not in the uploaded OAT file/i)) return { label: 'Not in OAT file', detail: s }
  return { label: 'Other', detail: s }
}

// Icons (mirrors the current app modal + mockup).
const MagIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round">
    <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
  </svg>
)
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
)
const ExtLinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h6v6" /><path d="M10 14L21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
  </svg>
)

// Types
interface DataIssue {
  // RAW issue string, kept verbatim — known-issue matching substring-matches against it.
  field: string
  severity: 'error' | 'warning'
  page: string // "Offer Details (EN)" — page + language
  // Standardized classification + supplementary detail (from classifyIssue).
  label: string
  detail: string
  status?: string // HTTP status for marketing-link findings
  linkLabel?: string
  linkUrl?: string
  // Type / Vehicle columns
  type: string | null // APR / Lease / Cash / Multi Vehicle / …
  vehicle: string | null // pretty vehicle name, or null
  multi: boolean // multi-vehicle offer → show "Multiple"
  // Offer identity + links
  offerId: string | null
  offerUrl?: string // offer-detail page (Context/Vehicle link + mobile external-link icon)
  screenshotUrl?: string // proof screenshot for the thumbnail + modal
  // Metadata for the shared image modal (openImageModal)
  tda: string
  tdaName: string
  language: string
  pageType: string
  offerType?: string
}

interface VIPCashLabel {
  type: string
  fullLabel: string
  amount: string
}

interface VIPCashLabelResults {
  heroLabel: string | null
  offerLabels: VIPCashLabel[]
  hasAnyLabel: boolean
}

interface VisualResult {
  page: string
  language: string
  baselineUrl?: string
  currentUrl?: string
  diffUrl?: string
  matchPercentage: number
  offerId?: string
  offerType?: string
  isCurrentBaseline?: boolean
  approvedBy?: string
  approvedAt?: number
  vipCashLabels?: VIPCashLabelResults
  // Document-absolute top (CSS px) of a pane's swapped section, measured at capture,
  // plus the capture devicePixelRatio — used to crop pane thumbnails to the
  // differentiating content exactly (section position varies per TDA/page length)
  paneAnchorTop?: number
  devicePixelRatio?: number
}

interface TDAResult {
  tda: string
  tdaName: string
  status: 'passed' | 'warned' | 'failed'
  dataIssues: DataIssue[]
  // Informational messages (expected content changes the semantic-pass policy downgraded)
  notes: string[]
  visualResults: VisualResult[]
  // Offer-detail pages actually checked for this TDA (summed across languages), for the all-clear line
  offersChecked?: number
}

interface AuditReport {
  id: string
  application: 'BAT' | 'RCT'
  timestamp: string
  summary: {
    total: number
    passed: number
    warned: number
    failed: number
  }
  results: TDAResult[]
}

interface DiffViewData {
  tda: string
  tdaName: string
  page: string
  language: string
  baselineUrl?: string
  currentUrl?: string
  diffUrl?: string
  offerId?: string
  offerType?: string
  matchPercentage: number
  isCurrentBaseline?: boolean
  approvedBy?: string
  approvedAt?: number
}

interface KnownIssue {
  issueId: string
  tda: string
  page: string
  issueType: string
  description: string
  isActive: boolean
}

interface MarkAsKnownIssueData {
  tda: string
  issueIndex: number
  field: string
  currentValue: string
}

// The rows /offers-audit/results returns, grouped by TDA (only the fields
// transformResults reads).
interface BackendAuditRow {
  status?: string
  offerId?: string
  offersCovered?: { checked?: number }
  page: string
  language: string
  offerType?: string
  offerUrl?: string
  baselineScreenshotUrl?: string
  currentScreenshotUrl?: string
  diffImageUrl?: string
  diffPercent?: number | null
  isCurrentBaseline?: boolean
  approvedBy?: string
  approvedAt?: number
  domChecks?: {
    vipCashLabels?: VisualResult['vipCashLabels']
    paneAnchorTop?: VisualResult['paneAnchorTop']
    devicePixelRatio?: VisualResult['devicePixelRatio']
  }
  issues?: string[]
  notes?: string[]
}

interface BackendAuditData {
  resultsByTda?: Record<string, BackendAuditRow[]>
  application?: AuditReport['application']
  completedAt?: string
}

export default function OffersAudit() {
  const params = useParams()
  const location = useLocation()

  // Extract sharedJobId from URL patterns: /audit/:jobId or /offers-audit/results/:jobId
  // The /audit/:jobId route populates params.jobId directly.
  // The /offers-audit/* wildcard route puts the rest path in params['*'], so we parse it.
  const sharedJobId = params.jobId || (() => {
    const wildcard = params['*'] || ''
    const match = wildcard.match(/^results\/([a-f0-9-]{36})$/)
    return match ? match[1] : null
  })()

  // Configuration state. The tool is RCT-only now (BAT/buyatoyota.com is sunset); the
  // type stays 'BAT'|'RCT' for backend/history compatibility but the UI hard-defaults RCT
  // and no longer offers a selector.
  const [application] = useState<'BAT' | 'RCT'>('RCT')
  const [selectedTDAs, setSelectedTDAs] = useState<string[]>([])
  const [languages, setLanguages] = useState<('en' | 'es')[]>(['en'])
  const [pages, setPages] = useState<('homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser')[]>(['offers'])
  const [excelFile, setExcelFile] = useState<File | null>(null)
  const [showOATInfo, setShowOATInfo] = useState(false)
  const [showPagesInfo, setShowPagesInfo] = useState(false)

  // Job state
  const [jobId, setJobId] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle')
  const [progress, setProgress] = useState(0)
  const [statusMessage, setStatusMessage] = useState('')
  const [liveCounts, setLiveCounts] = useState<{ completed: number; total: number } | null>(null)
  const [results, setResults] = useState<AuditReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  // UI state
  const [selectedDiff, setSelectedDiff] = useState<DiffViewData | null>(null)
  const [expandedTDAs, setExpandedTDAs] = useState<Set<string>>(new Set())
  const [toast, setToast] = useState<string | null>(null)
  const [showKnownIssues, setShowKnownIssues] = useState<Set<string>>(new Set())
  const [showInstructions, setShowInstructions] = useState(false)
  // Track approvals in current session (will be replaced by backend state on refresh)
  const [sessionApprovals, setSessionApprovals] = useState<Set<string>>(new Set())

  // Image modal state
  const [imageModal, setImageModal] = useState<{
    isOpen: boolean
    currentIndex: number
    images: Array<{
      url: string
      tda: string
      tdaName: string
      page: string
      language: string
      offerId?: string
      offerType?: string
    }>
    zoomLevel: number
  }>({
    isOpen: false,
    currentIndex: 0,
    images: [],
    zoomLevel: 1,
  })

  // Check for instructions page and handle navigation on location change
  const onRouteChange = useEffectEvent(() => {
    setShowInstructions(location.pathname.includes('/instructions'))

    // If navigating back to home (/offers-audit) from results page, reset state
    if (location.pathname === '/offers-audit' || location.pathname === '/offers-audit/') {
      if (jobId || results || status !== 'idle') {
        setJobId(null)
        setResults(null)
        setStatus('idle')
        setProgress(0)
        setStatusMessage('')
        setError(null)
      }
    }
  })
  useEffect(() => {
    onRouteChange()
  }, [location.pathname])

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [toast])

  // Image modal handlers
  const openImageModal = (
    url: string,
    tda: string,
    tdaName: string,
    page: string,
    language: string,
    offerId?: string,
    offerType?: string,
    allImages?: Array<{
      url: string
      tda: string
      tdaName: string
      page: string
      language: string
      offerId?: string
      offerType?: string
    }>
  ) => {
    const images = allImages || [{ url, tda, tdaName, page, language, offerId, offerType }]
    const currentIndex = images.findIndex((img) => img.url === url)
    setImageModal({
      isOpen: true,
      currentIndex: currentIndex >= 0 ? currentIndex : 0,
      images,
      zoomLevel: 1,
    })
  }

  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      currentIndex: 0,
      images: [],
      zoomLevel: 1,
    })
  }

  const navigateImage = (direction: 'prev' | 'next') => {
    setImageModal((prev) => {
      const newIndex =
        direction === 'prev'
          ? Math.max(0, prev.currentIndex - 1)
          : Math.min(prev.images.length - 1, prev.currentIndex + 1)
      return {
        ...prev,
        currentIndex: newIndex,
        zoomLevel: 1, // Reset zoom when navigating
      }
    })
  }

  const handleZoom = (delta: number) => {
    setImageModal((prev) => ({
      ...prev,
      zoomLevel: Math.max(0.5, Math.min(3, prev.zoomLevel + delta)),
    }))
  }

  // Keyboard controls for image modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!imageModal.isOpen) return

      switch (e.key) {
        case 'Escape':
          closeImageModal()
          break
        case 'ArrowLeft':
          navigateImage('prev')
          break
        case 'ArrowRight':
          navigateImage('next')
          break
        case '+':
        case '=':
          handleZoom(0.25)
          break
        case '-':
        case '_':
          handleZoom(-0.25)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [imageModal.isOpen, imageModal.currentIndex, imageModal.images.length])

  // Keyboard controls for diff modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedDiff) return

      if (e.key === 'Escape') {
        setSelectedDiff(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedDiff])

  // Data Issues table sorting + per-row expand (keyed by tda#index)
  const [issueSort, setIssueSort] = useState<{ key: 'page' | 'type' | 'vehicle' | 'label' | 'id'; dir: 1 | -1 } | null>(null)
  const [expandedIssues, setExpandedIssues] = useState<Record<string, boolean>>({})

  // Known Issues modal state
  const [knownIssueModal, setKnownIssueModal] = useState<MarkAsKnownIssueData | null>(null)
  const [knownIssueType, setKnownIssueType] = useState<string>('data_mismatch')
  const [knownIssueDescription, setKnownIssueDescription] = useState<string>('')
  const [isSubmittingKnownIssue, setIsSubmittingKnownIssue] = useState(false)

  // Track known issues per TDA (simulated - would come from API in real implementation)
  const [localKnownIssues, setLocalKnownIssues] = useState<Map<string, KnownIssue[]>>(new Map())

  // History panel state
  const [showHistoryPanel, setShowHistoryPanel] = useState(false)

  // Delete confirmation modal state
  const [deleteModalJobId, setDeleteModalJobId] = useState<string | null>(null)
  const [lastDeletedJobId, setLastDeletedJobId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Shared audit state
  const [auditNotFound, setAuditNotFound] = useState(false)
  const [isLoadingSharedAudit, setIsLoadingSharedAudit] = useState(false)

  // Polling ref
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // AbortController ref for cancellation (scoped to component instance)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Cleanup polling and abort controller on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
    }
  }, [])

  // Load shared audit on mount if sharedJobId is provided (once per id, not
  // whenever the loader is recreated)
  const loadShared = useEffectEvent((id: string) => loadSharedAudit(id))
  useEffect(() => {
    if (sharedJobId) {
      loadShared(sharedJobId)
    }
  }, [sharedJobId])

  // Function to load a shared audit by jobId
  const loadSharedAudit = async (auditJobId: string) => {
    setIsLoadingSharedAudit(true)
    setAuditNotFound(false)
    setJobId(auditJobId)

    try {
      // First check the job status
      const statusData = await getAuditStatus(auditJobId)

      if (!statusData || statusData.error) {
        // Job not found
        setAuditNotFound(true)
        setStatus('idle')
        return
      }

      if (statusData.status === 'completed') {
        // Load full results
        try {
          const resultsData = await getAuditResults(auditJobId)
          const transformedResults = transformResults(resultsData, auditJobId)
          setResults(transformedResults)
          setStatus('completed')
          // Expand all TDAs with issues
          if (transformedResults) {
            const expandedSet = new Set<string>()
            transformedResults.results.forEach((r: TDAResult) => {
              if (r.status !== 'passed') {
                expandedSet.add(r.tda)
              }
            })
            setExpandedTDAs(expandedSet)
          }
          // Update URL to the shareable format
          window.history.replaceState({}, '', `/offers-audit/results/${auditJobId}`)
        } catch (err) {
          if (err instanceof Error && err.message.includes('404')) {
            setAuditNotFound(true)
            setStatus('idle')
          } else {
            throw err
          }
        }
      } else if (statusData.status === 'failed') {
        setStatus('failed')
        setError(statusData.message || 'This audit failed during processing.')
        // Update URL to the shareable format
        window.history.replaceState({}, '', `/offers-audit/results/${auditJobId}`)
      } else if (statusData.status === 'processing' || statusData.status === 'pending') {
        // Audit is still in progress - start polling
        setStatus('processing')
        setProgress(statusData.progress || 0)
        setStatusMessage(statusData.message || 'Processing audit...')
        startPolling(auditJobId)
        // Update URL to the shareable format
        window.history.replaceState({}, '', `/offers-audit/results/${auditJobId}`)
      }
    } catch (err) {
      console.error('Error loading shared audit:', err)
      // Check if it's a 404 error
      if (err instanceof Error && err.message.includes('not found')) {
        setAuditNotFound(true)
        setStatus('idle')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load shared audit')
        setStatus('failed')
      }
    } finally {
      setIsLoadingSharedAudit(false)
    }
  }

  // Copy share URL to clipboard
  const handleCopyShareUrl = () => {
    if (!jobId) return

    const shareUrl = `${window.location.origin}/offers-audit/results/${jobId}`
    navigator.clipboard.writeText(shareUrl).then(() => {
      showToast('Share URL copied to clipboard!')
    }).catch(() => {
      showToast('Failed to copy URL')
    })
  }

  // Close tooltips when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      // Check if click is outside any tooltip
      if (!target.closest('[data-tooltip]')) {
        setShowOATInfo(false)
        setShowPagesInfo(false)
      }
    }

    if (showOATInfo || showPagesInfo) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showOATInfo, showPagesInfo])

  // File dropzone for Excel upload
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setExcelFile(acceptedFiles[0])
      setError(null)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  })

  const removeFile = () => {
    setExcelFile(null)
  }

  // TDA selection handlers
  const toggleTDA = (code: string) => {
    setSelectedTDAs((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    )
  }

  const selectAllTDAs = () => {
    if (selectedTDAs.length === TDA_LIST.length) {
      setSelectedTDAs([])
    } else {
      setSelectedTDAs(TDA_LIST.map((tda) => tda.code))
    }
  }

  // Language toggle
  const toggleLanguage = (lang: 'en' | 'es') => {
    setLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
    )
  }

  // Page toggle
  const togglePage = (page: 'homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser') => {
    setPages((prev) =>
      prev.includes(page) ? prev.filter((p) => p !== page) : [...prev, page]
    )
  }

  // Start audit
  const handleStartAudit = async () => {
    // TDA selection is required for both BAT and RCT (both are per-TDA)
    if (selectedTDAs.length === 0) {
      setError('Please select at least one TDA')
      return
    }
    if (languages.length === 0) {
      setError('Please select at least one language')
      return
    }
    if (pages.length === 0) {
      setError('Please select at least one page')
      return
    }

    setError(null)
    setStatus('processing')
    setProgress(0)
    setStatusMessage('Starting audit...')
    setResults(null)
    setLiveCounts(null)

    // Create new abort controller for this audit
    abortControllerRef.current = new AbortController()

    try {
      const data = await startOffersAudit({
        application,
        // Both BAT and RCT are per-TDA, so send the selected regions for either.
        tdas: selectedTDAs,
        languages,
        pages,
        excelFile: excelFile || undefined,
      })

      setJobId(data.jobId)

      // Put the job in the URL immediately (not just on completion) so a page refresh
      // mid-run re-attaches to the still-running job instead of dropping back to the form.
      window.history.replaceState({}, '', `/offers-audit/results/${data.jobId}`)

      // Start polling
      startPolling(data.jobId)
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // Cancelled by user
        return
      }
      setError(err instanceof Error ? err.message : 'Failed to start audit')
      setStatus('failed')
    }
  }

  // Poll for status
  const startPolling = (id: string) => {
    pollingRef.current = setInterval(async () => {
      try {
        const data = await getAuditStatus(id)
        setProgress(data.progress || 0)
        setStatusMessage(data.message || '')
        // Server-computed "X of Y complete" for the current activity (workers on
        // fan-out runs, phase counters on single-region runs). Replaces the old
        // frontend region-counting, which read "4 of 4 regions complete" the moment
        // every TDA had ANY results — misleading mid-run on two-language audits.
        setLiveCounts(data.liveCounts ?? null)

        // Results are withheld until the run completes. We deliberately do NOT fetch
        // partial results mid-run anymore: showing them was misleading (counts and
        // findings shift as workers report in), so the dashboard only appears on
        // completion below.

        if (data.status === 'completed') {
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
          setStatus('completed')
          setLiveCounts(null)
          // Fetch full results
          const resultsData = await getAuditResults(id)
          // Transform backend results to frontend format
          const transformedResults = transformResults(resultsData, id)
          setResults(transformedResults)
          // Expand all TDAs with issues
          if (transformedResults) {
            const expandedSet = new Set<string>()
            transformedResults.results.forEach((r: TDAResult) => {
              if (r.status !== 'passed') {
                expandedSet.add(r.tda)
              }
            })
            setExpandedTDAs(expandedSet)
          }
          // Update URL to the shareable format
          window.history.replaceState({}, '', `/offers-audit/results/${id}`)
        } else if (data.status === 'failed') {
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
          setStatus('failed')
          setLiveCounts(null)
          // Use the message from the backend if available, otherwise provide a generic message
          setError(data.message || 'The audit encountered an error during processing. Please try again.')
          // Update URL to the shareable format even for failed audits
          window.history.replaceState({}, '', `/offers-audit/results/${id}`)
        }
      } catch (err) {
        console.error('Polling error:', err)
      }
    }, 2000)
  }

  // Transform backend results to frontend format. getAuditResults is typed as
  // AuditReport but returns the backend's resultsByTda shape, hence unknown.
  const transformResults = (raw: unknown, id: string): AuditReport => {
    const backendData = raw as BackendAuditData
    const tdaResults: TDAResult[] = []

    if (backendData.resultsByTda) {
      for (const [tda, tdaResultsArray] of Object.entries(backendData.resultsByTda)) {
        const tdaName = TDA_LIST.find((t) => t.code === tda)?.name || tda

        // Determine overall TDA status
        const hasFail = tdaResultsArray.some((r) => r.status === 'fail' || r.status === 'error')
        const hasWarn = tdaResultsArray.some((r) => r.status === 'warn')
        const overallStatus = hasFail ? 'failed' : hasWarn ? 'warned' : 'passed'

        // Collect data issues, informational notes, and visual results
        const dataIssues: DataIssue[] = []
        const notes: string[] = []
        const visualResults: VisualResult[] = []
        let offersChecked = 0

        for (const result of tdaResultsArray) {
          // Synthetic per-language coverage rows carry the offers-checked count; sum and skip them
          // (they are not real results — status pass, no issues).
          if (result.offerId === '__coverage__') {
            offersChecked += result.offersCovered?.checked || 0
            continue
          }
          // Add visual result — except aggregate triage-note rows ('__notlive__'), which carry
          // data issues only (there is no page/screenshot behind them to tile).
          // If there's no baseline (first run), set matchPercentage to -1 to indicate "no baseline"
          // Otherwise calculate the actual match percentage from diffPercent
          const hasBaseline = !!result.baselineScreenshotUrl
          let matchPercentage: number
          if (!hasBaseline) {
            matchPercentage = -1 // Special value indicating no baseline
          } else if (result.diffPercent != null) {
            matchPercentage = computeMatchPercentage(result.diffPercent)
          } else {
            matchPercentage = 100
          }

          if (result.offerId !== '__notlive__') visualResults.push({
            page: result.page,
            language: result.language,
            baselineUrl: result.baselineScreenshotUrl,
            currentUrl: result.currentScreenshotUrl,
            diffUrl: result.diffImageUrl,
            matchPercentage,
            offerId: result.offerId,
            offerType: result.offerType,
            isCurrentBaseline: result.isCurrentBaseline || false,
            approvedBy: result.approvedBy,
            approvedAt: result.approvedAt,
            vipCashLabels: result.domChecks?.vipCashLabels,
            paneAnchorTop: result.domChecks?.paneAnchorTop,
            devicePixelRatio: result.domChecks?.devicePixelRatio,
          })

          // Convert issues to data issues format. The retired special-offer-image RCT
          // warning is filtered here too so it disappears from stored runs (the backend
          // no longer emits it for new runs).
          const pageLabel = `${formatPageName(result.page)} (${(result.language || 'en').toUpperCase()})`
          const typeLabel = formatType(result.offerType, result.offerId)
          for (const issue of result.issues || []) {
            if (issue.includes('special offer image elements but none are loaded')) continue
            const cls = classifyIssue(issue)
            const veh = parseVehicleName(result.offerType, result.offerId, issue)
            dataIssues.push({
              field: issue,
              severity: result.status === 'fail' ? 'error' : 'warning',
              page: pageLabel,
              label: cls.label,
              detail: cls.detail,
              status: cls.status,
              linkLabel: cls.linkLabel,
              linkUrl: cls.linkUrl,
              type: typeLabel,
              vehicle: veh.vehicle,
              multi: veh.multi,
              offerId: result.offerId || null,
              offerUrl: result.offerUrl,
              screenshotUrl: result.currentScreenshotUrl,
              tda,
              tdaName,
              language: result.language || 'en',
              pageType: result.page,
              offerType: result.offerType,
            })
          }

          // Collect informational notes (expected content changes that passed semantically)
          for (const note of result.notes || []) {
            notes.push(`${formatPageName(result.page)} (${(result.language || 'en').toUpperCase()}): ${note}`)
          }
        }

        tdaResults.push({
          tda,
          tdaName,
          status: overallStatus,
          dataIssues,
          notes,
          visualResults,
          offersChecked,
        })
      }
    }

    return {
      id,
      application: backendData.application || 'BAT',
      timestamp: backendData.completedAt || new Date().toISOString(),
      summary: {
        total: tdaResults.length,
        passed: tdaResults.filter((r) => r.status === 'passed').length,
        warned: tdaResults.filter((r) => r.status === 'warned').length,
        failed: tdaResults.filter((r) => r.status === 'failed').length,
      },
      results: tdaResults,
    }
  }

  // Cancel audit
  const handleCancelAudit = async () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    if (jobId) {
      try {
        await cancelAudit(jobId)
      } catch (err) {
        // Ignore cancel errors - job may have already completed
        console.error('Cancel error:', err)
      }
    }

    setStatus('idle')
    setJobId(null)
    setProgress(0)
    setStatusMessage('')
    // Return to a clean form. The backend already marked the job failed/cancelled (which
    // also stops the fan-out workers), so clearing the URL prevents a refresh from
    // re-attaching to the cancelled job.
    window.history.replaceState({}, '', '/offers-audit')
  }

  // Handle delete audit
  const handleDeleteAudit = async (auditJobId: string) => {
    setIsDeleting(true)
    try {
      await deleteAudit(auditJobId)
      setLastDeletedJobId(auditJobId)
      setToast('Audit deleted successfully')
      setDeleteModalJobId(null)

      // If we're viewing this audit's results, go back to idle state
      if (jobId === auditJobId) {
        setStatus('idle')
        setJobId(null)
        setResults(null)
      }
    } catch (err) {
      setToast(`Failed to delete audit: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsDeleting(false)
    }
  }

  // Toggle TDA expansion
  const toggleTDAExpansion = (tda: string) => {
    setExpandedTDAs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(tda)) {
        newSet.delete(tda)
      } else {
        newSet.add(tda)
      }
      return newSet
    })
  }

  // Open the Mark as Known Issue modal
  const openKnownIssueModal = (tda: string, issueIndex: number, field: string, currentValue: string) => {
    setKnownIssueModal({ tda, issueIndex, field, currentValue })
    setKnownIssueType('data_mismatch')
    setKnownIssueDescription(`${field}: ${currentValue}`)
  }

  // Submit the known issue
  const handleSubmitKnownIssue = async () => {
    if (!knownIssueModal) return

    setIsSubmittingKnownIssue(true)
    try {
      const data = await apiSaveKnownIssue({
        tda: knownIssueModal.tda,
        page: 'offers', // Default to offers page for data issues
        issueType: knownIssueType,
        description: knownIssueDescription,
        isActive: true,
      })

      // Update local state to track the known issue
      const newKnownIssue: KnownIssue = {
        issueId: data.issueId,
        tda: knownIssueModal.tda,
        page: 'offers',
        issueType: knownIssueType,
        description: knownIssueDescription,
        isActive: true,
      }

      setLocalKnownIssues((prev) => {
        const newMap = new Map(prev)
        const tdaIssues = newMap.get(knownIssueModal.tda) || []
        newMap.set(knownIssueModal.tda, [...tdaIssues, newKnownIssue])
        return newMap
      })

      showToast(`Marked issue as known for ${knownIssueModal.tda}`)
      setKnownIssueModal(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save known issue')
    } finally {
      setIsSubmittingKnownIssue(false)
    }
  }

  // Deactivate a known issue
  const handleDeactivateKnownIssue = async (tda: string, issueId: string) => {
    try {
      await apiSaveKnownIssue({
        tda,
        page: 'offers',
        issueType: issueId.split('#')[2] || 'unknown',
        description: 'Deactivated',
        isActive: false,
      })

      // Update local state
      setLocalKnownIssues((prev) => {
        const newMap = new Map(prev)
        const tdaIssues = newMap.get(tda) || []
        newMap.set(
          tda,
          tdaIssues.map((ki) => (ki.issueId === issueId ? { ...ki, isActive: false } : ki))
        )
        return newMap
      })

      showToast(`Deactivated known issue for ${tda}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate known issue')
    }
  }

  // Toggle known issues visibility for a TDA
  const toggleKnownIssuesVisibility = (tda: string) => {
    setShowKnownIssues((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(tda)) {
        newSet.delete(tda)
      } else {
        newSet.add(tda)
      }
      return newSet
    })
  }

  // Approve as baseline
  const handleApproveAsBaseline = async (
    tda: string,
    language: 'en' | 'es',
    page: 'homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser',
    offerId?: string
  ) => {
    if (!jobId) return

    try {
      const result = await saveBaseline({
        jobId,
        tda,
        language,
        page,
        offerId,
      })

      // Mark this item as approved in current session (will persist via backend on next load)
      const approvalKey = `${tda}#${language}#${page}${offerId ? `#${offerId}` : ''}`
      setSessionApprovals((prev) => new Set(prev).add(approvalKey))

      const offerSuffix = offerId ? ` (Offer ${offerId})` : ''
      showToast(`Approved ${result.baselinesSaved} baseline(s) for ${tda}${offerSuffix}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve baseline')
    }
  }

  // Export results
  const handleExport = async (format: 'csv' | 'json' | 'pdf') => {
    if (!results || !jobId) return

    try {
      const result = await exportAuditResults({ jobId, format })

      if (format === 'pdf') {
        // PDF is served from a presigned S3 URL to avoid API Gateway response size limits
        const { downloadUrl, filename } = result as { downloadUrl: string; filename: string }
        const a = document.createElement('a')
        a.href = downloadUrl
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else {
        const blob = result as Blob
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const date = new Date().toISOString().split('T')[0]
        const shortJobId = jobId.substring(0, 8)
        a.download = `offers-audit-${application}-${shortJobId}-${date}.${format}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }

      showToast(`Exported as ${format.toUpperCase()}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
    }
  }

  const showToast = (message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 3000)
  }

  // Mono "passed/total" callout next to a TDA name. Total = offers checked (the coverage count);
  // passed = those minus the distinct offers that have a finding (pane/offers-page findings carry a
  // btype:/cat: slug or no offerId and don't count against an offer). Null when no offers were checked.
  const tdaRatio = (result: TDAResult): string | null => {
    const total = result.offersChecked || 0
    if (total <= 0) return null
    const problemOffers = new Set(
      result.dataIssues
        .map((d) => d.offerId)
        .filter((id): id is string => !!id && !id.startsWith('btype:') && !id.startsWith('cat:') && id !== '__coverage__')
    )
    const passed = Math.max(0, total - problemOffers.size)
    return `${passed}/${total}`
  }

  // Get status badge class
  const getStatusBadgeClass = (status: 'passed' | 'warned' | 'failed') => {
    switch (status) {
      case 'passed':
        return 'badge-success'
      case 'warned':
        return 'badge-warning'
      case 'failed':
        return 'badge-error'
    }
  }

  // Load a past audit from the history panel
  const loadPastAudit = async (historyItem: AuditHistoryItem) => {
    try {
      setShowHistoryPanel(false)
      setJobId(historyItem.jobId)

      // For failed jobs, show the failure state with details
      if (historyItem.status === 'failed') {
        setStatus('failed')
        setError(`Audit failed on ${new Date(historyItem.createdAt).toLocaleString()}. The audit may have encountered an error during screenshot capture or processing.`)
        window.history.replaceState({}, '', `/offers-audit/results/${historyItem.jobId}`)
        return
      }

      setStatus('processing')
      setStatusMessage('Loading audit results...')

      const resultsData = await getAuditResults(historyItem.jobId)
      const transformedResults = transformResults(resultsData, historyItem.jobId)
      setResults(transformedResults)
      setStatus('completed')
      // Expand all TDAs with issues
      if (transformedResults) {
        const expandedSet = new Set<string>()
        transformedResults.results.forEach((r: TDAResult) => {
          if (r.status !== 'passed') {
            expandedSet.add(r.tda)
          }
        })
        setExpandedTDAs(expandedSet)
      }
      window.history.replaceState({}, '', `/offers-audit/results/${historyItem.jobId}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load past audit')
      setStatus('failed')
    }
  }

  // Format approval tooltip
  const getApprovalTooltip = (approvedBy?: string, approvedAt?: number) => {
    if (!approvedBy || !approvedAt) return undefined
    const date = new Date(approvedAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
    return `Approved by ${approvedBy} on ${date}`
  }

  // Render configuration panel
  const renderConfigPanel = () => (
    <div className="max-w-[1400px] mx-auto">
      <div className="card">
        {/* TDA Multi-Select (RCT is per-TDA) */}
        <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="label mb-0">TDA Selection</label>
              <button
                type="button"
                onClick={selectAllTDAs}
                className="text-sm text-linear-accent hover:text-linear-accent-hover transition-colors"
              >
                {selectedTDAs.length === TDA_LIST.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2 p-4">
              {TDA_LIST.map((tda) => (
                <label
                  key={tda.code}
                  title={tda.name}
                  className={`flex items-center justify-center px-3 py-2 rounded cursor-pointer transition-colors text-center ${
                    selectedTDAs.includes(tda.code)
                      ? 'bg-linear-accent/15 border border-linear-accent/40 text-linear-text'
                      : 'bg-linear-bg-card border border-linear-border hover:bg-linear-bg-hover text-linear-text-secondary'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedTDAs.includes(tda.code)}
                    onChange={() => toggleTDA(tda.code)}
                    className="w-3 h-3 text-linear-accent focus:ring-0 border-linear-border-input rounded-sm mr-1.5 bg-linear-bg-input"
                  />
                  <span className="text-xs font-medium">{tda.code}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-linear-text-muted mt-2">
              {selectedTDAs.length} of {TDA_LIST.length} TDAs selected
            </p>
          </div>

        {/* Language Selection - RCT supports EN + ES (via toyota.com/espanol/) */}
        <div className="mb-6">
            <label className="label">Languages</label>
            <div className="flex space-x-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={languages.includes('en')}
                  onChange={() => toggleLanguage('en')}
                  className="w-4 h-4 text-linear-accent focus:ring-linear-accent-focus border-linear-border-input rounded bg-linear-bg-input"
                />
                <span className="ml-2 text-linear-text">English</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={languages.includes('es')}
                  onChange={() => toggleLanguage('es')}
                  className="w-4 h-4 text-linear-accent focus:ring-linear-accent-focus border-linear-border-input rounded bg-linear-bg-input"
                />
                <span className="ml-2 text-linear-text">Spanish</span>
              </label>
            </div>
          </div>

        {/* Page Selection */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <label className="label mb-0">Pages to Check</label>
            <div className="relative" data-tooltip>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowPagesInfo(!showPagesInfo); setShowOATInfo(false); }}
                className="w-5 h-5 rounded-full bg-linear-bg-hover hover:bg-linear-text-muted/20 text-linear-text-muted text-xs font-bold flex items-center justify-center transition-colors"
                title="What each page checks"
              >
                i
              </button>
              {showPagesInfo && (
                <div className="absolute left-0 top-7 z-20 w-80 p-4 bg-linear-bg-card border border-linear-border-input rounded-lg shadow-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-sm font-semibold text-linear-text">Page Checks</h4>
                    <button
                      type="button"
                      onClick={() => setShowPagesInfo(false)}
                      className="text-linear-text-muted hover:text-linear-text"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="text-xs text-linear-text-secondary space-y-2">
                    <p><span className="font-semibold text-linear-text">Offers Page:</span> Offer cards, BAT-routed links (buyatoyota.com), OAT vs tool cross-check</p>
                    <p><span className="font-semibold text-linear-text">Offer Details:</span> Vehicle images, special offer images, color walk & 360 overlay</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={pages.includes('offers')}
                onChange={() => togglePage('offers')}
                className="w-4 h-4 text-linear-accent focus:ring-linear-accent-focus border-linear-border-input rounded bg-linear-bg-input"
              />
              <span className="ml-2 text-linear-text">Offers Page</span>
            </label>
            {/* Offer Details = DATA verification against the OAT export (expiration / value /
                images) PLUS a vehicle-detail screenshot per offer (keyed by offerId so
                results track the offer, not the monthly-rotating URL). Needs an Excel upload. */}
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={pages.includes('offerDetails')}
                onChange={() => togglePage('offerDetails')}
                className="w-4 h-4 text-linear-accent focus:ring-linear-accent-focus border-linear-border-input rounded bg-linear-bg-input"
              />
              <span className="ml-2 text-linear-text">
                Offer Details (data check + screenshots)
              </span>
            </label>
          </div>
        </div>

        {/* Excel Upload */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-3">
            <label className="label mb-0">
              OAT Parser Excel File{' '}
              <span className="text-linear-text-muted">(optional)</span>
            </label>
            <div className="relative" data-tooltip>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowOATInfo(!showOATInfo); setShowPagesInfo(false); }}
                className="w-5 h-5 rounded-full bg-linear-bg-hover hover:bg-linear-text-muted/20 text-linear-text-muted text-xs font-bold flex items-center justify-center transition-colors"
                title="What is OAT Parser?"
              >
                i
              </button>
              {showOATInfo && (
                <div className="absolute left-0 top-7 z-20 w-80 p-4 bg-linear-bg-card border border-linear-border-input rounded-lg shadow-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-sm font-semibold text-linear-text">About Parser Files</h4>
                    <button
                      type="button"
                      onClick={() => setShowOATInfo(false)}
                      className="text-linear-text-muted hover:text-linear-text"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="text-xs text-linear-text-secondary space-y-2">
                    <p>
                      <strong className="text-linear-text">OAT Parser</strong> is our offer data parser tool.
                      It extracts offer data and validates for common issues.
                    </p>
                    <p>
                      The export includes validation flags like null values, $0 disposition fees, missing images,
                      and links that route to buyatoyota.com.
                    </p>
                    <p className="pt-2 border-t border-linear-border">
                      Upload the OAT Parser export to enable data validation alongside the page checks.
                      The tool also cross-checks the export's <code className="bg-linear-bg-input px-1 rounded text-linear-accent text-xs">hasBATLinks</code> flag
                      against the buyatoyota.com-routed links it detects on the live page and surfaces any disagreement as an <code className="bg-linear-bg-input px-1 rounded text-linear-accent text-xs">[OAT vs TOOL]</code> note.
                    </p>
                  </div>
                  <a
                    href="https://main.d3pxwy7ni2jojl.amplifyapp.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-1 text-xs text-linear-accent hover:text-linear-accent-hover mt-3"
                  >
                    <span>Open OAT Parser Tool</span>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}
            </div>
          </div>
          {!excelFile ? (
            <div
              {...getRootProps()}
              className={`upload-zone ${isDragActive ? 'drag-active' : ''}`}
            >
              <input {...getInputProps()} />
              <svg
                className="w-10 h-10 text-linear-text-muted mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-linear-text-secondary">
                {isDragActive
                  ? 'Drop the Excel file here...'
                  : 'Drag and drop OAT Parser Excel file, or click to select'}
              </p>
              <p className="text-xs text-linear-text-muted mt-1">.xlsx or .xls (max 10MB)</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="p-4 bg-linear-bg-hover border border-linear-border-input rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <svg
                    className="w-8 h-8 text-success"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-linear-text">{excelFile.name}</p>
                    <p className="text-xs text-linear-text-muted">
                      {(excelFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-linear-text-muted hover:text-error transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/30 rounded-lg text-error">
            {error}
          </div>
        )}

        {/* Start Button */}
        <button
          onClick={handleStartAudit}
          className="btn btn-primary w-full"
          disabled={selectedTDAs.length === 0 || languages.length === 0 || pages.length === 0}
        >
          Start {application} Audit
        </button>

      </div>
    </div>
  )

  // Render progress section
  const renderProgressSection = () => (
    <div className="max-w-md mx-auto text-center">
      <div className="card">
        <div className="spinner spinner-lg mx-auto mb-4"></div>
        <p className="text-linear-text font-medium mb-2">{statusMessage || 'Processing audit...'}</p>
        <div className="progress-bar mb-2">
          <div
            className="progress-bar-fill"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="text-sm text-linear-text-muted mb-4">{progress}%</p>
        {liveCounts && liveCounts.total > 0 && (
          <p className="text-sm text-linear-text-muted mb-4">
            {liveCounts.completed} of {liveCounts.total} complete
          </p>
        )}
        <button onClick={handleCancelAudit} className="btn btn-outline">
          Cancel
        </button>
      </div>
    </div>
  )

  // ---- Data Issues: sortable table (desktop) + card list (mobile) ----
  const sortIssueBy = (k: 'page' | 'type' | 'vehicle' | 'label' | 'id') =>
    setIssueSort((prev) => (prev && prev.key === k ? { key: k, dir: prev.dir === 1 ? -1 : 1 } : { key: k, dir: 1 }))
  const issueArrow = (k: string) => (issueSort && issueSort.key === k ? (issueSort.dir === 1 ? ' ▲' : ' ▼') : '')
  const sortVal = (x: DataIssue, k: 'page' | 'type' | 'vehicle' | 'label' | 'id') =>
    k === 'vehicle' ? (x.multi ? 'Multiple' : x.vehicle || '~~~') : k === 'type' ? x.type || '~~~' : k === 'id' ? (x.offerId || '~~~') : ((x[k] as string) || '~~~')

  const issueLead = (issue: DataIssue) => (
    <>
      {issue.label}
      {issue.status ? <span className="text-linear-text-muted"> ({issue.status})</span> : null}
    </>
  )
  const sevBadge = (issue: DataIssue) => (
    <span className={`badge ${issue.severity === 'error' ? 'badge-error' : 'badge-warning'}`}>{issue.severity}</span>
  )
  const knownBtn = (issue: DataIssue) => (
    <button
      className="oa-known"
      title="Mark as Known"
      aria-label="Mark as Known"
      onClick={(e) => {
        e.stopPropagation()
        openKnownIssueModal(issue.tda, 0, issue.field, 'Issue detected')
      }}
    >
      <CheckIcon />
    </button>
  )
  const vehLink = (issue: DataIssue, text: string) =>
    issue.offerUrl ? (
      <a
        href={issue.offerUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-linear-accent underline"
        title="Open the offer page (live — may have rotated out)"
        onClick={(e) => e.stopPropagation()}
      >
        {text}
      </a>
    ) : (
      text
    )
  const idLink = (issue: DataIssue) =>
    issue.offerId === '__notlive__' ? (
      // Aggregate pre-MYCO/rotation note rows have no real offer behind them — show a plain
      // human label instead of the internal slug, and never link anywhere.
      <>Not Live</>
    ) : issue.offerId ? (
      issue.offerUrl ? (
        <a
          href={issue.offerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="oa-id-link"
          title="Open the offer page (live — may have rotated out)"
          onClick={(e) => e.stopPropagation()}
        >
          {issue.offerId}
        </a>
      ) : (
        <>{issue.offerId}</>
      )
    ) : (
      <span className="text-linear-text-muted">-</span>
    )
  const renderDetailBody = (issue: DataIssue) =>
    issue.linkUrl ? (
      // The label is the clickable link (icon in place of "open link →"); the raw URL lives in the
      // tooltip since we're linking to it anyway. Falls back to the URL only if no label was captured.
      <a
        href={issue.linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="oa-detail-link"
        title={issue.linkUrl}
        onClick={(e) => e.stopPropagation()}
      >
        <ExtLinkIcon />
        <span>{issue.linkLabel || issue.linkUrl}</span>
      </a>
    ) : (
      <div className="text-linear-text-muted">{issue.detail}</div>
    )
  const renderThumb = (issue: DataIssue, mode: 'modal' | 'newtab') => {
    if (!issue.screenshotUrl) return null
    return (
      <span
        className="oa-thumb-wrap"
        onClick={(e) => {
          e.stopPropagation()
          if (mode === 'newtab') window.open(issue.screenshotUrl!, '_blank', 'noopener')
          else
            openImageModal(
              issue.screenshotUrl!,
              issue.tda,
              issue.tdaName,
              issue.pageType,
              issue.language,
              issue.offerId || undefined,
              issue.offerType
            )
        }}
      >
        <img className="oa-thumb" src={issue.screenshotUrl} alt="screenshot" />
        <span className="oa-thumb-overlay">
          <MagIcon />
        </span>
      </span>
    )
  }

  const renderIssueRow = (issue: DataIssue, id: string) => {
    const open = !!expandedIssues[id]
    const hasDetailBody = !!issue.linkUrl || !!issue.detail
    // Only rows with real detail (link/text) or a screenshot expand. ID-only rows (e.g. "Offer
    // page not found") don't expand — no chevron, no pointer, no toggle. Their ID still relocates
    // into the row at narrow widths via the always-rendered, CSS-gated oa-id-in-detail line.
    const expandable = hasDetailBody || !!issue.screenshotUrl
    const chevClass = `oa-chev${open ? ' open' : ''}`
    return (
      <tr
        key={id}
        className={`oa-row${expandable ? ' oa-clickable' : ''}`}
        onClick={
          expandable
            ? () => {
                if (window.getSelection && String(window.getSelection())) return // don't collapse mid text-select
                setExpandedIssues((p) => ({ ...p, [id]: !p[id] }))
              }
            : undefined
        }
      >
        <td className="oa-col-chev"><div className="oa-line oa-line-center">{expandable && <span className={chevClass}>▸</span>}</div></td>
        <td className="oa-col-page oa-nowrap"><div className="oa-line">{issue.page}</div></td>
        <td className="oa-col-type"><div className="oa-line">{issue.type ? issue.type : <span className="text-linear-text-muted">-</span>}</div></td>
        <td className="oa-col-veh">
          <div className="oa-line">{issue.multi ? vehLink(issue, 'Multiple') : issue.vehicle ? vehLink(issue, issue.vehicle) : <span className="text-linear-text-muted">-</span>}</div>
        </td>
        <td className="oa-issue-cell">
          <div className="oa-issuebody">
            <div className="oa-line">
              <span className="oa-lead">
                {issueLead(issue)}
                <span className="oa-sev-inline">{sevBadge(issue)}</span>
              </span>
            </div>
            {/* ID relocates here when the ID column is hidden (narrow). Always rendered; CSS shows it
                only at narrow widths, so it doesn't depend on the row expanding. */}
            <div className="oa-id-in-detail oa-id-mono">{idLink(issue)}</div>
            {open && (
              <div className="oa-detail">
                {hasDetailBody && renderDetailBody(issue)}
                {renderThumb(issue, 'modal')}
              </div>
            )}
          </div>
        </td>
        <td className="oa-col-id oa-id-mono"><div className="oa-line">{idLink(issue)}</div></td>
        <td className="oa-col-sev"><div className="oa-line">{sevBadge(issue)}</div></td>
        <td className="oa-col-known"><div className="oa-line oa-line-center">{knownBtn(issue)}</div></td>
      </tr>
    )
  }

  const renderIssueCard = (issue: DataIssue, id: string) => {
    const hasOffer = issue.multi || !!issue.vehicle
    const hasSub = hasOffer || !!issue.type
    const sub = `${issue.multi ? 'Multiple' : issue.vehicle || ''}${hasOffer && issue.type ? ' · ' : ''}${issue.type || ''}`
    return (
      <div key={id} className="oa-card">
        <div className="oa-card-head">
          <div className="oa-lead oa-card-lead">{issueLead(issue)}</div>
          <div className="oa-card-right">
            {sevBadge(issue)}
            {hasOffer && issue.offerUrl && (
              <a
                className="oa-known"
                href={issue.offerUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open offer page"
                aria-label="Open offer page"
              >
                <ExtLinkIcon />
              </a>
            )}
            {knownBtn(issue)}
          </div>
        </div>
        {hasSub && <div className="oa-card-sub">{sub}</div>}
        {(!!issue.linkUrl || !!issue.detail || !!issue.screenshotUrl) && (
          <div className="oa-detail oa-open">
            {(!!issue.linkUrl || !!issue.detail) && renderDetailBody(issue)}
            {renderThumb(issue, 'newtab')}
          </div>
        )}
        {issue.offerId && <div className="oa-card-id oa-id-mono">{idLink(issue)}</div>}
      </div>
    )
  }

  const renderIssuesPanel = (issues: DataIssue[], tda: string) => {
    const withIds = issues.map((issue, i) => ({ issue, id: `${tda}#${i}` }))
    const sorted = issueSort
      ? [...withIds].sort((a, b) => {
          const xa = sortVal(a.issue, issueSort.key).toLowerCase()
          const xb = sortVal(b.issue, issueSort.key).toLowerCase()
          return xa < xb ? -issueSort.dir : xa > xb ? issueSort.dir : 0
        })
      : withIds
    return (
      <div className="mt-4">
        <div className="oa-issues">
        <div className="oa-table-wrap">
          <table className="oa-table">
            <thead>
              <tr>
                <th className="oa-col-chev"></th>
                <th className="oa-col-page oa-sortable" onClick={() => sortIssueBy('page')}>Page{issueArrow('page')}</th>
                <th className="oa-col-type oa-sortable" onClick={() => sortIssueBy('type')}>Type{issueArrow('type')}</th>
                <th className="oa-col-veh oa-sortable" onClick={() => sortIssueBy('vehicle')}>Vehicle(s){issueArrow('vehicle')}</th>
                <th className="oa-sortable" onClick={() => sortIssueBy('label')}>Issue{issueArrow('label')}</th>
                <th className="oa-col-id oa-sortable" onClick={() => sortIssueBy('id')}>ID{issueArrow('id')}</th>
                <th className="oa-col-sev">Severity</th>
                <th className="oa-col-known">Known</th>
              </tr>
            </thead>
            <tbody>{sorted.map(({ issue, id }) => renderIssueRow(issue, id))}</tbody>
          </table>
        </div>
        <div className="oa-cards">{sorted.map(({ issue, id }) => renderIssueCard(issue, id))}</div>
        </div>
      </div>
    )
  }

  // Render results dashboard. When inProgress is true the audit is still
  // running, so we stream in the TDA cards but hide the export/share/delete bar
  // (export 400s until the job completes; delete mid-run would be destructive).
  const renderResultsDashboard = (inProgress = false) => {
    if (!results) return null

    return (
      <div>
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card text-center">
            <div className="text-3xl font-bold text-linear-text">{results.summary.total}</div>
            <div className="text-sm text-linear-text-muted">Total TDAs</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-success">{results.summary.passed}</div>
            <div className="text-sm text-linear-text-muted">Passed</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-warning">{results.summary.warned}</div>
            <div className="text-sm text-linear-text-muted">Warned</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-error">{results.summary.failed}</div>
            <div className="text-sm text-linear-text-muted">Failed</div>
          </div>
        </div>

        {/* Toggle + Export/Share/Delete bar. The toggle stays visible mid-run; the
            destructive/export actions are hidden until the job completes. */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          {/* All TDAs are always shown; the per-TDA passed/total callout + status badge convey
              health at a glance, so there's no problems-only toggle. */}
          <span />

          {!inProgress && (
          <div className="flex space-x-2">
          {/* Share Button */}
          {jobId && (
            <button
              onClick={handleCopyShareUrl}
              className="btn btn-outline text-sm py-2 flex items-center space-x-1"
              title="Copy shareable link to clipboard"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              <span>Share</span>
            </button>
          )}
          {/* Delete Button */}
          {jobId && (
            <button
              onClick={() => setDeleteModalJobId(jobId)}
              className="btn btn-outline text-sm py-2 flex items-center space-x-1 text-error border-error/30 hover:bg-error/10"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span>Delete</span>
            </button>
          )}
          {/* Export Dropdown */}
          <div className="relative group">
            <button className="btn btn-outline text-sm py-2 flex items-center space-x-1">
              <span>Export</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div className="dropdown absolute right-0 mt-2 w-32 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
              <button
                onClick={() => handleExport('csv')}
                className="dropdown-item w-full text-left"
              >
                CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="dropdown-item w-full text-left"
              >
                JSON
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="dropdown-item w-full text-left"
              >
                PDF
              </button>
            </div>
          </div>
          </div>
          )}
        </div>

        {/* TDA Results Cards. Every TDA is shown. Passing TDAs are inert (the passed/total
            callout tells the story); only warned/failed TDAs expand to the Data Issues table. */}
        <div className="space-y-4">
          {results.results.map((result) => {
            const expandable = result.status !== 'passed'
            const ratio = tdaRatio(result)
            return (
            <div
              key={result.tda}
              className={`card !p-0 ${
                result.status === 'passed'
                  ? 'border-l-4 border-l-success'
                  : result.status === 'warned'
                  ? 'border-l-4 border-l-warning'
                  : 'border-l-4 border-l-error'
              }`}
            >
              {/* TDA Header. Passing TDAs are non-interactive (disabled, no chevron); only
                  warned/failed TDAs toggle open. No overflow-hidden on the card — it clipped the
                  ratio tooltip — so the header button rounds its own corners to match the card. */}
              <button
                type="button"
                disabled={!expandable}
                className={`w-full flex items-center justify-between gap-4 p-4 text-left transition-colors rounded-t-md ${
                  expandedTDAs.has(result.tda) ? '' : 'rounded-b-md'
                } ${
                  expandable ? 'cursor-pointer hover:bg-linear-bg-hover' : 'cursor-default'
                }`}
                onClick={expandable ? () => toggleTDAExpansion(result.tda) : undefined}
              >
                <div className="flex items-center space-x-3">
                  <span className="font-mono font-bold text-linear-text">{result.tda}</span>
                  {result.tda !== result.tdaName && (
                    <span className="text-linear-text-secondary">{result.tdaName}</span>
                  )}
                  {ratio && (
                    <span
                      className={`tooltip-wrapper font-mono text-sm ${
                        result.status === 'passed'
                          ? 'text-success'
                          : result.status === 'warned'
                          ? 'text-warning'
                          : 'text-error'
                      }`}
                      data-tooltip="Offers without findings / offers checked in this region"
                    >
                      {ratio}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {/* No chevron on the TDA header row (Yashar 2026-07-14) — the whole row is still
                      clickable to expand/collapse; only item rows inside keep their chevrons. */}
                  <span className={`badge ${getStatusBadgeClass(result.status)}`}>
                    {result.status.charAt(0).toUpperCase() + result.status.slice(1)}
                  </span>
                </div>
              </button>

              {/* Expanded Content */}
              {expandable && expandedTDAs.has(result.tda) && (
                <div className="px-4 pb-4 border-t border-linear-border">
                  {/* Informational Notes (expected content changes that passed semantically) */}
                  {result.notes.length > 0 && (
                    <div className="mt-4 p-3 rounded bg-blue-50 border border-blue-200">
                      <h4 className="text-sm font-semibold text-blue-700 mb-1">Notes</h4>
                      <ul className="text-xs text-blue-700 space-y-0.5 list-disc list-inside">
                        {result.notes.map((note, idx) => (
                          <li key={idx}>{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Data Issues — sortable/collapsible table (desktop) + cards (mobile).
                      Screenshots now live inside each expanded row, so the old Visual Results
                      tiles section below was removed. */}
                  {result.dataIssues.length > 0 && renderIssuesPanel(result.dataIssues, result.tda)}


                  {/* Known Issues Panel */}
                  {(localKnownIssues.get(result.tda)?.filter(ki => ki.isActive).length ?? 0) > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-linear-text">
                          Known Issues ({localKnownIssues.get(result.tda)?.filter(ki => ki.isActive).length || 0})
                        </h4>
                        <button
                          onClick={() => toggleKnownIssuesVisibility(result.tda)}
                          className="text-xs text-linear-accent hover:text-linear-accent-hover flex items-center space-x-1"
                        >
                          <span>{showKnownIssues.has(result.tda) ? 'Hide' : 'Show'}</span>
                          <svg
                            className={`w-3 h-3 transition-transform ${showKnownIssues.has(result.tda) ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                      {showKnownIssues.has(result.tda) && (
                        <div className="bg-info/10 border border-info/30 rounded-lg p-3">
                          <div className="space-y-2">
                            {localKnownIssues.get(result.tda)?.filter(ki => ki.isActive).map((ki) => (
                              <div
                                key={ki.issueId}
                                className="flex items-center justify-between bg-linear-bg-card p-2 rounded border border-linear-border"
                              >
                                <div className="flex-1">
                                  <span className="badge badge-info">
                                    {ki.issueType.replace(/_/g, ' ')}
                                  </span>
                                  <p className="text-sm text-linear-text-secondary mt-1">{ki.description}</p>
                                </div>
                                <button
                                  onClick={() => handleDeactivateKnownIssue(result.tda, ki.issueId)}
                                  className="ml-2 text-xs text-error hover:opacity-80 flex items-center"
                                  title="Deactivate this known issue"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-info mt-2">
                            These issues are marked as known and will be skipped in future audits.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {result.dataIssues.length === 0 && (
                    <p className="mt-4 text-sm text-linear-text-muted">
                      {result.offersChecked && result.offersChecked > 0
                        ? `Checked ${result.offersChecked} offer${result.offersChecked === 1 ? '' : 's'}, all passed.`
                        : 'No problems found for this TDA.'}
                    </p>
                  )}
                </div>
              )}
            </div>
            )
          })}
        </div>

        {/* New Audit CTA */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              setStatus('idle')
              setResults(null)
              setJobId(null)
              setProgress(0)
              setStatusMessage('')
            }}
            className="btn btn-primary text-lg px-8 py-3"
          >
            Run New Audit
          </button>
        </div>
      </div>
    )
  }

  // Render image viewer modal
  const renderImageModal = () => {
    if (!imageModal.isOpen || imageModal.images.length === 0) return null

    const currentImage = imageModal.images[imageModal.currentIndex]
    const canGoPrev = imageModal.currentIndex > 0
    const canGoNext = imageModal.currentIndex < imageModal.images.length - 1

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
        onClick={closeImageModal}
      >
        {/* Modal content */}
        <div
          className="relative w-full h-full flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with metadata */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold">
                  {currentImage.tdaName} - {formatPageName(currentImage.page)}
                </h3>
                {currentImage.offerType && (
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium ${
                      currentImage.offerType === 'apr'
                        ? 'bg-info text-white'
                        : currentImage.offerType === 'lease'
                        ? 'bg-linear-accent text-white'
                        : currentImage.offerType === 'cash'
                        ? 'bg-success text-white'
                        : currentImage.offerType === 'miscellaneous'
                        ? 'bg-warning text-white'
                        : currentImage.offerType === 'multiple_vehicle'
                        ? 'bg-error text-white'
                        : currentImage.offerType === 'category' || currentImage.offerType === 'browseType'
                        ? 'bg-info text-white'
                        : 'bg-linear-border text-white'
                    }`}
                  >
                    {formatOfferTypeBadge(currentImage.offerType, currentImage.offerId)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                {/* Image counter */}
                {imageModal.images.length > 1 && (
                  <span className="text-sm">
                    {imageModal.currentIndex + 1} / {imageModal.images.length}
                  </span>
                )}
                {/* Close button */}
                <button
                  onClick={closeImageModal}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  title="Close (ESC)"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Main image area — 10% breathing room on the left/right (desktop). */}
          <div className="flex-1 overflow-auto px-4 md:px-[10vw] pt-20 pb-24">
            <div className="min-h-full flex items-center justify-center">
              <img
                src={currentImage.url}
                alt={`${currentImage.tda} ${currentImage.page}`}
                className="max-w-full h-auto"
                style={{
                  transform: `scale(${imageModal.zoomLevel})`,
                  transformOrigin: 'center center',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </div>
          </div>

          {/* Bottom controls */}
          <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black via-black/90 to-transparent p-6 pb-4">
            <div className="flex items-center justify-between text-white">
              {/* Metadata */}
              <div className="flex items-center gap-4 text-sm">
                <span>
                  <span className="text-linear-text-muted">TDA:</span> {currentImage.tda}
                </span>
                <span>
                  <span className="text-linear-text-muted">Language:</span> {currentImage.language.toUpperCase()}
                </span>
                {currentImage.offerId && (
                  <span className="text-linear-text-muted">
                    ID: <span className="text-white font-mono text-xs">{currentImage.offerId}</span>
                  </span>
                )}
              </div>

              {/* Zoom and Download controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleZoom(-0.25)}
                  disabled={imageModal.zoomLevel <= 0.5}
                  className="p-2 hover:bg-white/20 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Zoom out (-)"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>
                <span className="text-sm w-16 text-center">{Math.round(imageModal.zoomLevel * 100)}%</span>
                <button
                  onClick={() => handleZoom(0.25)}
                  disabled={imageModal.zoomLevel >= 3}
                  className="p-2 hover:bg-white/20 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Zoom in (+)"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>
                <button
                  onClick={() => setImageModal((prev) => ({ ...prev, zoomLevel: 1 }))}
                  className="p-2 hover:bg-white/20 rounded transition-colors text-xs"
                  title="Reset zoom"
                >
                  Reset
                </button>
                <div className="w-px h-6 bg-white/30 mx-1" />
                <a
                  href={currentImage.url}
                  download={`${currentImage.tda}_${currentImage.page}_${currentImage.language}${currentImage.offerId ? '_' + currentImage.offerId : ''}.png`}
                  className="flex items-center gap-1 px-3 py-2 hover:bg-white/20 rounded transition-colors text-sm"
                  title="Download image"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span>Download</span>
                </a>
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          {imageModal.images.length > 1 && (
            <>
              {/* Left arrow */}
              {canGoPrev && (
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors text-white"
                  title="Previous (leftarrow)"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              {/* Right arrow */}
              {canGoNext && (
                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full transition-colors text-white"
                  title="Next (rightarrow)"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  // Render diff viewer modal
  const renderDiffModal = () => {
    if (!selectedDiff) return null

    return (
      <div className="modal-overlay" onClick={() => setSelectedDiff(null)}>
        <div className="modal max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="modal-header">
            <div>
              <h3 className="text-lg font-semibold text-linear-text">
                {selectedDiff.tda} - {selectedDiff.tdaName}
              </h3>
              <p className="text-sm text-linear-text-muted">
                {formatPageName(selectedDiff.page)} ({selectedDiff.language.toUpperCase()}) -{' '}
                {selectedDiff.matchPercentage === -1 ? (
                  'No baseline'
                ) : (
                  <span title={MATCH_TOOLTIP} className="cursor-help">
                    {formatMatchLabel(selectedDiff.matchPercentage)}
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => setSelectedDiff(null)}
              className="text-linear-text-muted hover:text-linear-text transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Modal Content */}
          <div className="modal-body max-h-[60vh]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Baseline */}
              <div>
                <h4 className="text-sm font-medium text-linear-text-secondary mb-2">Baseline</h4>
                <div className="aspect-video bg-linear-bg-input rounded-lg overflow-hidden border border-linear-border">
                  {selectedDiff.baselineUrl ? (
                    <img
                      src={selectedDiff.baselineUrl}
                      alt="Baseline screenshot"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-linear-text-muted">
                      No baseline available
                    </div>
                  )}
                </div>
              </div>

              {/* Current */}
              <div>
                <h4 className="text-sm font-medium text-linear-text-secondary mb-2">Current</h4>
                <div className="aspect-video bg-linear-bg-input rounded-lg overflow-hidden border border-linear-border">
                  {selectedDiff.currentUrl ? (
                    <img
                      src={selectedDiff.currentUrl}
                      alt="Current screenshot"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-linear-text-muted">
                      No screenshot available
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Diff Overlay */}
            {selectedDiff.diffUrl && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-linear-text-secondary mb-2">Difference Overlay</h4>
                <div className="aspect-video bg-linear-bg-input rounded-lg overflow-hidden border border-linear-border">
                  <img
                    src={selectedDiff.diffUrl}
                    alt="Diff overlay"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button onClick={() => setSelectedDiff(null)} className="btn btn-outline">
              Close
            </button>
            {(() => {
              const approvalKey = `${selectedDiff.tda}#${selectedDiff.language}#${selectedDiff.page}${selectedDiff.offerId ? `#${selectedDiff.offerId}` : ''}`
              const isApproved = selectedDiff.isCurrentBaseline || sessionApprovals.has(approvalKey)
              const isDisabled = isApproved
              const tooltipText = isApproved
                ? getApprovalTooltip(selectedDiff.approvedBy, selectedDiff.approvedAt)
                : undefined

              return (
                <span className="flex gap-2" title={tooltipText}>
                  <button
                    onClick={async () => {
                      if (isDisabled) return
                      if (!window.confirm(`Set as global baseline for ${selectedDiff.tda} (${selectedDiff.page} · ${selectedDiff.language.toUpperCase()})?\n\nThis affects every team member's future audits.`)) return
                      await handleApproveAsBaseline(selectedDiff.tda, selectedDiff.language as 'en' | 'es', selectedDiff.page as 'homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser', selectedDiff.offerId)
                      setSelectedDiff(null)
                    }}
                    disabled={isDisabled}
                    className={`btn ${isApproved ? 'btn-secondary opacity-50 cursor-not-allowed' : 'btn-primary'}`}
                  >
                    {isApproved ? (
                      <>
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Baseline Set
                      </>
                    ) : (
                      'Set as Global Baseline'
                    )}
                  </button>
                  {(isApproved || selectedDiff.baselineUrl) && (
                    <button
                      onClick={async () => {
                        try {
                          await deleteBaseline({
                            tda: selectedDiff.tda,
                            language: selectedDiff.language as 'en' | 'es',
                            page: selectedDiff.page as 'homepage' | 'offers' | 'offerDetails' | 'vehicleInfoSupra' | 'vehicleInfoLandCruiser',
                            offerId: selectedDiff.offerId,
                            application: results?.application,
                          })
                          const approvalKey = `${selectedDiff.tda}#${selectedDiff.language}#${selectedDiff.page}${selectedDiff.offerId ? `#${selectedDiff.offerId}` : ''}`
                          setSessionApprovals((prev) => {
                            const next = new Set(prev)
                            next.delete(approvalKey)
                            return next
                          })
                          showToast('Baseline removed')
                          if (jobId) {
                            const resultsData = await getAuditResults(jobId)
                            const transformedResults = transformResults(resultsData, jobId)
                            setResults(transformedResults)
                          }
                          setSelectedDiff(null)
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Failed to remove baseline')
                        }
                      }}
                      className="btn bg-error/10 text-error hover:bg-error/15 border border-error/30"
                    >
                      Remove Baseline
                    </button>
                  )}
                </span>
              )
            })()}

          </div>
        </div>
      </div>
    )
  }

  // Render Mark as Known Issue modal
  const renderKnownIssueModal = () => {
    if (!knownIssueModal) return null

    return (
      <div className="modal-overlay" onClick={() => setKnownIssueModal(null)}>
        <div className="modal max-w-md w-full" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="modal-header">
            <div>
              <h3 className="text-lg font-semibold text-linear-text">Mark as Known Issue</h3>
              <p className="text-sm text-linear-text-muted">TDA: {knownIssueModal.tda}</p>
            </div>
            <button
              onClick={() => setKnownIssueModal(null)}
              className="text-linear-text-muted hover:text-linear-text transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Modal Content */}
          <div className="modal-body space-y-4">
            {/* Issue Type Dropdown */}
            <div>
              <label className="label">
                Issue Type
              </label>
              <select
                value={knownIssueType}
                onChange={(e) => setKnownIssueType(e.target.value)}
                className="select w-full"
              >
                <option value="data_mismatch">Data Mismatch</option>
                <option value="null_value">Null Value (Expected)</option>
                <option value="zero_disp_fee">Zero Disposition Fee (Expected)</option>
                <option value="missing_image">Missing Image (Expected)</option>
                <option value="visual_difference">Visual Difference (Acceptable)</option>
                <option value="temporary_issue">Temporary Issue</option>
                <option value="known_bug">Known Bug (Pending Fix)</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Description Textarea */}
            <div>
              <label className="label">
                Description
              </label>
              <textarea
                value={knownIssueDescription}
                onChange={(e) => setKnownIssueDescription(e.target.value)}
                className="textarea w-full"
                rows={4}
                placeholder="Describe why this issue is known and should be skipped..."
              />
              <p className="text-xs text-linear-text-muted mt-1">
                This description will be used to match similar issues in future audits.
              </p>
            </div>

            {/* Original Issue Info */}
            <div className="bg-linear-bg-hover p-3 rounded-lg border border-linear-border">
              <p className="text-xs font-medium text-linear-text-muted mb-1">Original Issue</p>
              <p className="text-sm text-linear-text-secondary">
                <span className="font-medium text-linear-text">{knownIssueModal.field}:</span> {knownIssueModal.currentValue}
              </p>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer bg-linear-bg-hover">
            <button
              onClick={() => setKnownIssueModal(null)}
              className="btn btn-outline"
              disabled={isSubmittingKnownIssue}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitKnownIssue}
              className="btn btn-primary"
              disabled={isSubmittingKnownIssue || !knownIssueDescription.trim()}
            >
              {isSubmittingKnownIssue ? (
                <span className="flex items-center space-x-2">
                  <span className="spinner spinner-sm"></span>
                  <span>Saving...</span>
                </span>
              ) : (
                'Mark as Known'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Render delete confirmation modal
  const renderDeleteConfirmModal = () => {
    if (!deleteModalJobId) return null

    return (
      <div className="modal-overlay" onClick={() => setDeleteModalJobId(null)}>
        <div className="modal max-w-md w-full" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="modal-header">
            <div className="flex items-center space-x-2">
              <svg className="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 className="text-lg font-semibold text-linear-text">Delete Audit</h3>
            </div>
            <button
              onClick={() => setDeleteModalJobId(null)}
              className="text-linear-text-muted hover:text-linear-text"
              disabled={isDeleting}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Modal Content */}
          <div className="modal-body">
            <p className="text-linear-text-secondary mb-2">
              Are you sure you want to delete this audit? This action cannot be undone.
            </p>
            <p className="text-sm text-linear-text-muted">
              This will permanently delete the audit results and all associated screenshots.
            </p>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer bg-linear-bg-hover">
            <button
              onClick={() => setDeleteModalJobId(null)}
              className="btn btn-outline"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={() => handleDeleteAudit(deleteModalJobId)}
              className="btn btn-danger"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className="flex items-center space-x-2">
                  <span className="spinner spinner-sm"></span>
                  <span>Deleting...</span>
                </span>
              ) : (
                'Delete Audit'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Render instructions screen
  if (showInstructions) {
    return (
      <OffersAuditInstructions
        onBack={() => setShowInstructions(false)}
      />
    )
  }

  // Breadcrumbs for AppLayout
  const breadcrumbs = results
    ? [
        { label: 'Offers Audit', href: '/offers-audit' },
        { label: 'Results' },
      ]
    : [
        { label: 'Offers Audit' },
      ]

  // Header actions
  const headerActions = (
    <div className="flex items-center gap-2">
      {results && (
        <div className="text-xs text-linear-text-muted">
          {application} - {new Date(results.timestamp).toLocaleString()}
        </div>
      )}
      <button
        onClick={() => setShowHistoryPanel(!showHistoryPanel)}
        className={`p-2 rounded-lg transition-colors ${
          showHistoryPanel
            ? 'text-linear-accent bg-linear-accent/10'
            : 'text-linear-text-secondary hover:text-linear-text hover:bg-linear-bg-hover'
        }`}
        title="Previous Audits"
        aria-label="Previous Audits"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
    </div>
  )

  return (
    <AppLayout
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
    >
      {/* Loading shared audit */}
      {isLoadingSharedAudit && (
        <div className="max-w-md mx-auto text-center">
          <div className="card">
            <div className="spinner spinner-lg mx-auto mb-4"></div>
            <p className="text-linear-text font-medium">Loading audit...</p>
          </div>
        </div>
      )}

      {/* Audit not found */}
      {auditNotFound && !isLoadingSharedAudit && (
        <div className="max-w-md mx-auto text-center">
          <div className="card">
            <svg
              className="w-16 h-16 text-linear-text-muted mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-linear-text mb-2">Audit Not Found</h2>
            <p className="text-linear-text-secondary mb-6">
              This audit may have been deleted or the URL is invalid.
            </p>
            <button
              onClick={() => {
                setAuditNotFound(false)
                setJobId(null)
                window.history.replaceState({}, '', '/offers-audit')
              }}
              className="btn btn-primary"
            >
              Start New Audit
            </button>
          </div>
        </div>
      )}

      {!isLoadingSharedAudit && !auditNotFound && status === 'idle' && (
        <>
          <div className="max-w-[1400px] mx-auto mb-8">
            <h1 className="text-2xl font-semibold text-linear-text mb-2">Offers Audit</h1>
            <p className="text-linear-text-muted">
              Automated visual regression testing and data validation for promotional offers
            </p>
          </div>
          {renderConfigPanel()}
        </>
      )}
      {!isLoadingSharedAudit && !auditNotFound && status === 'processing' && (
        // Results are intentionally withheld until the run completes. Showing
        // partial results mid-run was misleading (counts and findings keep
        // changing as workers report in), so while processing we show only the
        // progress section and reveal the full dashboard on completion.
        renderProgressSection()
      )}
      {!isLoadingSharedAudit && !auditNotFound && status === 'completed' && renderResultsDashboard()}
      {!isLoadingSharedAudit && !auditNotFound && status === 'failed' && (
        <div className="max-w-md mx-auto text-center">
          <div className="card">
            <svg
              className="w-16 h-16 text-error mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-linear-text mb-2">Audit Failed</h2>
            <p className="text-linear-text-secondary mb-6">{error || 'An error occurred during the audit.'}</p>
            <button
              onClick={() => {
                setStatus('idle')
                setError(null)
                setJobId(null)
                setResults(null)
                setProgress(0)
                setStatusMessage('')
                // Reset URL so refresh doesn't reload the failed audit
                window.history.replaceState({}, '', '/offers-audit')
              }}
              className="btn btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {renderImageModal()}

      {/* Diff Viewer Modal */}
      {renderDiffModal()}

      {/* Mark as Known Issue Modal */}
      {renderKnownIssueModal()}

      {/* Delete Confirmation Modal */}
      {renderDeleteConfirmModal()}

      {/* History Slide-out Panel */}
      {showHistoryPanel && (
        <HistoryPanel title="Previous Audits" onClose={() => setShowHistoryPanel(false)}>
          <OffersAuditHistory
            onDeleteRequest={(jobId) => setDeleteModalJobId(jobId)}
            onSelectJob={(item) => loadPastAudit(item)}
            embedded
            deletedJobId={lastDeletedJobId}
          />
        </HistoryPanel>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className="toast toast-success fixed bottom-4 right-4 animate-fade-in z-50">
          <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-linear-text">{toast}</span>
        </div>
      )}
    </AppLayout>
  )
}
